import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { eur } from "../../lib/format";
import { Loader2 } from "lucide-react";

export default function AdminPackages() {
  const [packages, setPackages] = useState(null);

  const load = () => api.get("/packages").then((r) => setPackages(r.data)).catch(() => setPackages([]));
  useEffect(() => { load(); }, []);

  if (packages === null) return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "#B08D57" }} /></div>;

  return (
    <div data-testid="admin-packages">
      <h2 className="font-heading text-2xl mb-5">Retreats</h2>
      <div className="grid md:grid-cols-2 gap-5">
        {packages.map((p) => <PackageCard key={p.slug} pkg={p} onSaved={load} />)}
      </div>
    </div>
  );
}

function PackageCard({ pkg, onSaved }) {
  const [price, setPrice] = useState(pkg.price_per_night_eur);
  const [tagline, setTagline] = useState(pkg.tagline);
  const [hero, setHero] = useState(pkg.hero_image);
  const [bookable, setBookable] = useState(pkg.bookable !== false);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/packages/${pkg.slug}`, {
        price_per_night_eur: parseFloat(price), tagline, hero_image: hero,
      });
      toast.success("Retreat updated");
      onSaved();
    } catch { toast.error("Update failed"); }
    finally { setSaving(false); }
  };

  const toggleBookable = async () => {
    const nextVal = !bookable;
    setToggling(true);
    setBookable(nextVal);
    try {
      await api.put(`/admin/packages/${pkg.slug}`, { bookable: nextVal });
      toast.success(nextVal ? `${pkg.name} is now bookable` : `${pkg.name} hidden from booking`);
      onSaved();
    } catch { toast.error("Could not update availability"); setBookable(!nextVal); }
    finally { setToggling(false); }
  };

  return (
    <div className="rounded-2xl border overflow-hidden bg-white" style={{ borderColor: "#E5E7EB" }} data-testid={`admin-package-${pkg.slug}`}>
      <div className="relative h-40 overflow-hidden">
        <img src={hero} alt="" className="w-full h-full object-cover" />
        {!bookable && <div className="absolute inset-0 bg-black/55 flex items-center justify-center"><span className="text-white text-xs uppercase tracking-widest border border-white/60 rounded-full px-3 py-1">Not bookable</span></div>}
      </div>
      <div className="p-5 space-y-4">
        <div>
          <h3 className="font-heading text-2xl">{pkg.name}</h3>
          <p className="text-xs" style={{ color: "#6B7280" }}>{pkg.area} · current {eur(pkg.price_per_night_eur)}/night</p>
        </div>

        <div className="flex items-center justify-between rounded-xl border px-4 py-3" style={{ borderColor: bookable ? "#B08D57" : "#E5E7EB", background: bookable ? "#FBF7F0" : "#F9FAFB" }}>
          <div>
            <p className="text-sm font-medium" style={{ color: "#121212" }}>Available as a booking destination</p>
            <p className="text-[11px]" style={{ color: "#6B7280" }}>{bookable ? "Shown in Design My Journey" : "Hidden from guests"}</p>
          </div>
          <button onClick={toggleBookable} disabled={toggling} data-testid={`pkg-bookable-${pkg.slug}`} aria-pressed={bookable}
            className="relative w-12 h-7 rounded-full transition-colors shrink-0" style={{ background: bookable ? "#B08D57" : "#D1D5DB" }}>
            <span className="absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition-transform" style={{ transform: bookable ? "translateX(20px)" : "translateX(0)" }} />
          </button>
        </div>

        <Field label="Tagline" value={tagline} onChange={setTagline} testid={`pkg-tagline-${pkg.slug}`} />
        <Field label="Price / night (EUR)" value={price} onChange={setPrice} type="number" testid={`pkg-price-${pkg.slug}`} />
        <Field label="Hero Image URL" value={hero} onChange={setHero} testid={`pkg-hero-${pkg.slug}`} />
        <button onClick={save} disabled={saving} data-testid={`pkg-save-${pkg.slug}`}
          className="w-full py-2.5 rounded-xl text-white flex items-center justify-center gap-2" style={{ background: "#111111" }}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider" style={{ color: "#6B7280" }}>{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full border rounded-lg px-3 py-2.5 mt-1 text-sm" style={{ borderColor: "#E5E7EB" }} />
    </div>
  );
}
